import { CellValueChangedEvent, PostSortRowsParams, RowClickedEvent } from "ag-grid-community";
import { RaonColDef } from "src/types/grid";
import { AgGridReact } from "ag-grid-react";
import React, { useCallback, useMemo, useRef } from "react";
import { aggridHeaderClass, SmallTitleWithSubSpan } from "src/components/common/common";
import Grid_Excel from "src/components/common/gridWrapper/Grid_Excel";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { useDetailTableEvents } from "src/hooks/detail-table/useDetailTableEvents";
import { createCellCssClassRules, GridCellConfig, postSortHandler } from "src/utils/grid/GridHandlerContent";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { defaultColDef, getDefaultRowIndexColumnConfig, getRowStyleFlags } from "src/utils/grid/gridStyleUtils";
import { formatGridDecimal1, formatGridInteger } from "src/utils/number/numberUtils";
import { showWarning } from "src/utils/toast/toastUtil";
import { defaultHRM2040_R01Dto, HRM2040_R01Dto } from "./Dto_HRM2040_R01";
import { useHRM2040_R01Store } from "./Store_HRM2040_R01";

/** 이 화면의 컬럼 배치·너비를 브라우저에 저장할 때 쓰는 키이다. */
const COLUMN_STATE_KEY = 'HRM2040_R01-grid-column-state';
/** 숫자 칸의 편집·합계·저장 검증에 사용할 필드다. */
export const NUMBER_COLUMN_FIELDS = [
    'totWorkDays', 'dayShiftDays', 'nightShiftDays', 'totWorkHours',
    'totOverHours', 'totNightHours', 'totHolidayHours', 'totOutHours',
    'outCount', 'totPubOutHours', 'absentDays', 'lateCount',
    'totLateMin', 'leaveEarlyCount', 'totEarlyMin',
] as const;


/** 숫자 칸과 비고 칸을 클릭 편집 대상으로 지정한다. */
const EDITABLE_COLUMNS: string[] = [...NUMBER_COLUMN_FIELDS, 'remark'];

/** Container가 편집 그리드에 전달하는 값이다. */
interface Props {
    clickable: boolean; // true이면 행 클릭으로 선택을 토글한다.
    setClickable: (clickable: boolean) => void; // 공통 그리드의 클릭 방지 상태를 되돌린다.
    gridRef: React.RefObject<AgGridReact<HRM2040_R01Dto> | null>; // 편집 확정과 스크롤 복원에 쓰는 그리드 참조
}

/**
 * 월근태 숫자·비고를 편집하고 15개 합계와 건수를 하단에 표시한다.
 * @param props 선택 상태와 그리드 참조
 * @returns 행 추가·삭제 버튼이 없는 편집 그리드와 영역 제목
 */
export const Table_HRM2040_R01 = (props: Props) => {
    // 구조 분해: Container의 클릭 상태와 그리드 참조를 꺼낸다.
    const { clickable, setClickable, gridRef } = props;
    // 화면 Store에서 행·원본·선택·컬럼 배치·스크롤 상태와 갱신 함수를 가져온다.
    const {
        HRM2040_R01List, setHRM2040_R01List, updateHRM2040_R01List,
        originalHRM2040_R01List, selectedHRM2040_R01List,
        addSelectedHRM2040_R01List, removeSelectedHRM2040_R01List,
        initSelectedHRM2040_R01List, allSelectedHRM2040_R01List,
        columnState, setColumnState, scrollTop, setScrollTop,
    } = useHRM2040_R01Store();
    // true이면 행 드래그 중이라 정렬 완료 처리를 건너뛴다.
    const isRowDraggingRef = useRef(false);



    // HRM2130과 같은 공통 그리드 설정으로 행 ID·행 번호·셀 스타일·표시 목록을 만든다.
    const {
        getRowId, createRowClassRules, createCellClassRules, paddedGridData, renderIndexCell,
    } = useGridSettingContent<HRM2040_R01Dto>({
        gridRef,
        key: 'valueIndex', // 조회 때 부여한 고유 행 번호로 그리드 행을 구분한다.
        gridData: HRM2040_R01List,
        defaultGridData: defaultHRM2040_R01Dto,
        selectedRow: selectedHRM2040_R01List,
        originalGridData: originalHRM2040_R01List,
        updateRow: updateHRM2040_R01List,
    });


    // 변경·선택·비활성·왼쪽 정렬을 공통 CSS 규칙에 넘길 컬럼 묶음이다.
    const cssCellConfig = useMemo<GridCellConfig>(() => ({
        disabledCells: [],
        insertedDisabledCells: [],
        commonDisabledCells: ['empNo', 'empNm', 'orgNm'],
        pointerCells: EDITABLE_COLUMNS,
        insertedPointerCells: [],
        commonPointerCells: [],
        alignLeftCells: ['empNm', 'orgNm', 'remark'],
    }), []);


    // 위 컬럼 묶음에서 공통 셀 규칙에 필요한 목록을 꺼낸다.
    const {
        disabledInsertedColumns, disabledColumns, pointerInsertedColumns, pointerColumns, alignLeftColumns,
    } = useMemo(() => createCellCssClassRules(cssCellConfig), [cssCellConfig]);

    // 공통 기본 컬럼 설정 위에 선택·비활성·정렬 규칙을 합친다.
    const cellCssDefaultColDef = useMemo(() => ({
        cellClassRules: createCellClassRules<HRM2040_R01Dto>({
            idKey: 'valueIndex',
            selectedList: selectedHRM2040_R01List.map(Number),
            deletedList: [],
            disabledInsertedColumns, disabledColumns,
            pointerInsertedColumns, pointerColumns, alignLeftColumns,
        }),
    }), [createCellClassRules, selectedHRM2040_R01List, disabledInsertedColumns, disabledColumns,
        pointerInsertedColumns, pointerColumns, alignLeftColumns]);

    /** 숫자 칸의 표시·편집과 문자 칸의 순서를 정한 컬럼 목록이다. */
    const baseColumnDefs = useMemo<RaonColDef<HRM2040_R01Dto>[]>(() => [
        // 공통 행 번호 컬럼을 먼저 넣고 +/- 함수에는 빈 함수를 넘긴다.
        ...getDefaultRowIndexColumnConfig(
            (params: HRM2040_R01Dto) => renderIndexCell(
                params,
                (innerParams: HRM2040_R01Dto) => getRowStyleFlags(innerParams, {
                    deletedList: [],
                    selectedList: selectedHRM2040_R01List,
                    dataList: HRM2040_R01List,
                }),
                () => {},
                () => {},
            ),
        ).map(column => ({ ...column })),
        { headerName: '사원번호', colId: 'empNo', field: 'empNo', width: 100 },
        { headerName: '사원명', colId: 'empNm', field: 'empNm', width: 84 },
        { headerName: '부서', colId: 'orgNm', field: 'orgNm', width: 96 },
        { headerName: '근무일수', colId: 'totWorkDays', field: 'totWorkDays', width: 76, editable: true, valueFormatter: formatGridInteger },
        { headerName: '주간', colId: 'dayShiftDays', field: 'dayShiftDays', width: 56,  editable: true, valueFormatter: formatGridInteger },
        { headerName: '야간', colId: 'nightShiftDays', field: 'nightShiftDays', width: 56,  editable: true, valueFormatter: formatGridInteger },
        { headerName: '정상시간', colId: 'totWorkHours', field: 'totWorkHours', width: 78,  editable: true, valueFormatter: formatGridDecimal1 },
        { headerName: '연장', colId: 'totOverHours', field: 'totOverHours', width: 78,  editable: true, valueFormatter: formatGridDecimal1 },
        { headerName: '야간', colId: 'totNightHours', field: 'totNightHours', width: 66,   editable: true, valueFormatter: formatGridDecimal1 },
        { headerName: '휴일', colId: 'totHolidayHours', field: 'totHolidayHours', width: 66,   editable: true, valueFormatter: formatGridDecimal1 },
        { headerName: '외출(제외)', colId: 'totOutHours', field: 'totOutHours', width: 84,    editable: true, valueFormatter: formatGridDecimal1 },
        { headerName: '외출횟수', colId: 'outCount', field: 'outCount', width: 72,  editable: true, valueFormatter: formatGridInteger },
        { headerName: '공용외출', colId: 'totPubOutHours', field: 'totPubOutHours', width: 76,  editable: true, valueFormatter: formatGridDecimal1 },
        { headerName: '결근일', colId: 'absentDays', field: 'absentDays', width: 66,  editable: true, valueFormatter: formatGridInteger },
        { headerName: '지각(회)', colId: 'lateCount', field: 'lateCount', width: 66,  editable: true, valueFormatter: formatGridInteger },
        { headerName: '지각(분)', colId: 'totLateMin', field: 'totLateMin', width: 70, editable: true, valueFormatter: formatGridInteger },
        { headerName: '조퇴(회)', colId: 'leaveEarlyCount', field: 'leaveEarlyCount', width: 66,  editable: true, valueFormatter: formatGridInteger },
        { headerName: '조퇴(분)', colId: 'totEarlyMin', field: 'totEarlyMin', width: 70,  editable: true, valueFormatter: formatGridInteger },
        { headerName: '비고', colId: 'remark', field: 'remark', width: 160, editable: true },
    ], [renderIndexCell, selectedHRM2040_R01List, HRM2040_R01List]);

    // 컬럼 정의에서 초기 배치를 만들고, 저장한 배치가 있으면 공통 복원 함수를 적용한다.
    const defaultColumnState = useMemo(() => buildDefaultColumnStateFromDefs(baseColumnDefs, []), [baseColumnDefs]);

    const columnDefs = useMemo(() => columnState === null ? baseColumnDefs : GridCustomColumnDefs<HRM2040_R01Dto>({
        baseColumnDefs,
        columnState,
        // 현재 컬럼의 제목은 baseColumnDefs를 사용하며, 삭제된 컬럼의 복원 제목은 colId로 표시한다.
        headerNames: {},
        fallbackHeaderClass: aggridHeaderClass,
    }), [baseColumnDefs, columnState]);

    /**
     * 숫자·비고 입력을 검사하고 유효한 값만 화면 목록에 반영한다.
     * @param event 편집 전후 값과 행을 포함한 그리드 이벤트
     * @returns 반환값은 없다.
     */
    const handleCellChange = useCallback((event: CellValueChangedEvent<HRM2040_R01Dto>) => {
        // 하단 합계 행과 데이터가 없는 행은 저장 목록에 반영하지 않는다.
        if (event.node.rowPinned || !event.data || !event.colDef.field) return;

        /** 편집된 DTO 필드 이름이다. */
        const field = event.colDef.field;
        if (field === 'remark') {
            /** 비고는 빈 값을 빈 문자열로 저장한다. */
            const remark = String(event.newValue ?? '');
            if (remark.length > 1000) {
                showWarning(`${event.data.empNm || event.data.empNo}: 비고는 1000자 이하여야 합니다.`);
                event.node.setData({ ...event.data, remark: event.oldValue });
                return;
            }
            if (remark === event.oldValue) return;

            updateHRM2040_R01List(event.data.valueIndex, {
                ...event.data,
                remark,
                isModified: true,
            });
            event.node.setData({ ...event.data, remark });
            return;
        }

        // 정규식: 입력값의 쉼표와 공백을 모두 제거한다.
        /** 숫자로 바꿀 입력 문자열이다. */
        const cleaned = String(event.newValue ?? '').replace(/[,\s]/g, '');
        /** 화면 목록에 저장할 숫자 값이다. */
        const numberValue = Number(cleaned);

        if (!cleaned || !Number.isFinite(numberValue) || numberValue < 0) {
            showWarning('0 이상의 숫자를 입력하세요.');
            event.node.setData({ ...event.data, [field]: event.oldValue });
            return;
        }

        // DB integer인 7개 칸은 소수 입력을 허용하지 않는다.
        if ([
            'dayShiftDays', 'nightShiftDays', 'outCount', 'lateCount',
            'totLateMin', 'leaveEarlyCount', 'totEarlyMin',
        ].includes(field) && !Number.isInteger(numberValue)) {
            showWarning('정수로 입력하세요.');
            event.node.setData({ ...event.data, [field]: event.oldValue });
            return;
        }

        if (numberValue === event.oldValue) return;

        // 사용자가 바꾼 숫자만 갱신하고 다른 집계값은 그대로 둔다.
        updateHRM2040_R01List(event.data.valueIndex, {
            ...event.data,
            [field]: numberValue,
            isModified: true,
        });
        event.node.setData({ ...event.data, [field]: numberValue });
    }, [updateHRM2040_R01List]);

    /**
     * 실제 행을 클릭하면 String(valueIndex)로 선택·해제한다.
     * @param event 클릭한 행 정보
     * @returns 반환값은 없다.
     */
    const handleRowClick = useCallback((event: RowClickedEvent<HRM2040_R01Dto>) => {
        if (event.node.rowPinned || !event.data?.empNo) return;
        if (!clickable) { setClickable(true); return; }
        /** 모든 선택 경로에서 같은 문자열로 비교할 행 식별값이다. */
        const id = String(event.data.valueIndex);
        if (selectedHRM2040_R01List.includes(id)) removeSelectedHRM2040_R01List([id]);
        else addSelectedHRM2040_R01List([id]);
    }, [clickable, setClickable, selectedHRM2040_R01List,
        removeSelectedHRM2040_R01List, addSelectedHRM2040_R01List]);

    // 편집 가능 칸의 클릭 편집과 방향키 이동을 공통 이벤트 훅에서 가져온다.
    const cellEventHandlers = useDetailTableEvents({
        detailList: HRM2040_R01List,
        editableColumns: EDITABLE_COLUMNS,
        keyDownConfig: { enableArrowNavigation: true },
    });

    /**
     * 사용자가 컬럼을 정렬하면 공통 정렬 규칙으로 목록 순서를 갱신한다.
     * @param params 정렬된 그리드 행
     * @returns 반환값은 없다.
     */
    const handlePostSortWithDrag = useCallback((params: PostSortRowsParams<HRM2040_R01Dto>) => {
        if (isRowDraggingRef.current) return;
        postSortHandler(params, HRM2040_R01List, originalHRM2040_R01List, setHRM2040_R01List);
    }, [HRM2040_R01List, originalHRM2040_R01List, setHRM2040_R01List]);


    // 현재 서버 목록의 건수와 15개 숫자 합계를 한 개의 하단 고정 행에 넣는다.
    const pinnedBottomRow = useMemo(() => {
        /** 건수와 숫자 합계를 담는 읽기 전용 표시 행이다. */
        const totalRow: HRM2040_R01Dto = {
            ...defaultHRM2040_R01Dto, // 행 타입의 비합계 필드는 기본값으로 둔다.
            empNo: `${HRM2040_R01List.length.toLocaleString()}건`,
        };
        for (const field of NUMBER_COLUMN_FIELDS) {
            totalRow[field] = HRM2040_R01List.reduce((sum, row) => sum + Number(row[field] ?? 0), 0);
        }
        return [totalRow];
    }, [HRM2040_R01List]);

    return (
        // 우클릭 메뉴·셀 계산·컬럼 상태 저장을 담당하는 HRM2130과 같은 공통 감싸기 컴포넌트
        <GridContextWrapper<HRM2040_R01Dto>
            enableCellCalculation gridRef={gridRef} paddedGridData={paddedGridData}
            initSelected={initSelectedHRM2040_R01List} defaultRowData={defaultHRM2040_R01Dto}
            allSelected={allSelectedHRM2040_R01List}
            baseColumnDefs={columnDefs} defaultColumnState={defaultColumnState}
            setColumnState={setColumnState} localStorageKey={COLUMN_STATE_KEY}
            menuItems={undefined}
        >
            {/* 공통 제목 오른쪽에 기존 월근태 집계 출처와 외출 시간 해석을 안내한다. */}
            <SmallTitleWithSubSpan title="월근태 집계 내역"
                subTitle="일일근태(외출·복귀 반영)를 월별로 합산 · 외출(제외) 시간은 이미 정상·연장시간에서 빠진 값" />
            {/* 조회·편집·선택·하단 건수와 합계를 표시하는 단일 그리드이다. */}
            <Grid_Excel<HRM2040_R01Dto>
                ref={gridRef}
                rowData={paddedGridData}
                getRowId={getRowId}
                columnDefs={columnDefs}
                defaultColDef={cellCssDefaultColDef}
                rowClassRules={createRowClassRules<HRM2040_R01Dto>({
                    idKey: 'valueIndex',
                    selectedList: selectedHRM2040_R01List.map(Number),
                    deletedList: [],
                })}
                onRowClicked={handleRowClick}
                onCellClicked={cellEventHandlers.handleCellClick}
                onCellKeyDown={cellEventHandlers.handleCellKeyDown}
                onCellValueChanged={handleCellChange}
                pinnedBottomRowData={pinnedBottomRow}
                onPostSortRows={handlePostSortWithDrag}
                columnStateKey={COLUMN_STATE_KEY}
                setColumnState={setColumnState}
                getDefaultColumnState={() => defaultColumnState}
                scrollTop={scrollTop}
                setScrollTop={setScrollTop}
            />
        </GridContextWrapper>
    );
};
